import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  Link,
  Divider,
  Box,
} from '@mui/material';
import { formatPrice } from '../utils/formatPrice';

const ProductDetailModal = ({ product, open, onClose }) => {
  if (!product) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "not_found":
        return "warning";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "not_found":
        return "Not Found";
      case "error":
        return "Error";
      default:
        return status;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          Product Details
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Product Name and Status */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                {product.product_name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
              <Chip
                label={getStatusLabel(product.status)}
                color={getStatusColor(product.status)}
                size="medium"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Basic Information */}
          <Typography variant="h6" gutterBottom color="primary">
            Basic Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography><strong>Manufacturer:</strong> {product.manufacturer || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>Model/SKU:</strong> {product.model_sku || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>Product Number:</strong> {product.product_number || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>Category:</strong> {product.category || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>E-shop:</strong> {product.eshop_name || "N/A"}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Pricing Information */}
          <Typography variant="h6" gutterBottom color="primary">
            Pricing Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography>
                <strong>Currency:</strong> {product.currency || "CZK"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>
                <strong>Price per piece:</strong> {formatPrice(product.price_per_piece, product.currency)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>
                <strong>Price per Wp:</strong> {formatPrice(product.price_per_wp, product.currency)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Stock Information */}
          <Typography variant="h6" gutterBottom color="primary">
            Stock Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Typography>
                <strong>Stock Quantity:</strong> {product.stock_quantity || "N/A"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Additional Information */}
          <Typography variant="h6" gutterBottom color="primary">
            Additional Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Typography>
                <strong>Product URL:</strong>{" "}
                {product.product_url ? (
                  <Link
                    href={product.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                  >
                    View on E-shop
                  </Link>
                ) : (
                  "N/A"
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>
                <strong>Unique ID:</strong> {product.unique_id || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>
                <strong>Last Updated:</strong>{" "}
                {product.last_updated
                  ? new Date(product.last_updated).toLocaleString()
                  : "N/A"
                }
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetailModal;